import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ArrowLeft, CreditCard, Lock, Calendar, Music, Star, ShieldCheck } from 'lucide-react';

/**
 * STATIC MOCKUP of how Stripe Payment Element would look embedded inside a Clubbers drawer.
 * No real Stripe calls, no real payment — purely visual preview.
 */
const PaymentMockupPreview = () => {
  const navigate = useNavigate();
  const [method, setMethod] = useState('card'); // card | apple | google
  const [cardNumber, setCardNumber] = useState('4242 4242 4242 4242');
  const [exp, setExp] = useState('12/28');
  const [cvc, setCvc] = useState('123');
  const [name, setName] = useState('Juan Pérez');

  const total = 20.0;
  const points = Math.floor(total * 10);

  return (
    <div className="fixed inset-0 z-[50] bg-[#0a0a0a] flex items-end justify-center">
      <div className="relative w-full max-w-md glass-dark rounded-t-3xl max-h-[95vh] overflow-y-auto">
        {/* Mock banner */}
        <div className="absolute top-0 left-0 right-0 z-10 bg-[#F82C47]/15 border-b border-[#F82C47]/30 py-2 px-4 flex items-center justify-center gap-2">
          <ShieldCheck size={14} className="text-[#F82C47]" />
          <span className="text-[#F82C47] text-xs font-semibold uppercase tracking-wide">Vista previa · Payment Element</span>
        </div>

        <div className="pt-12">
          {/* Handle */}
          <div className="flex justify-center pt-3 pb-2"><div className="w-10 h-1 bg-white/30 rounded-full" /></div>

          {/* Header */}
          <div className="flex items-center justify-between px-5 pb-4">
            <div className="flex items-center gap-2">
              <button onClick={() => navigate('/')} className="glass-dark w-9 h-9 rounded-full flex items-center justify-center text-white">
                <ArrowLeft size={18} />
              </button>
              <h2 className="text-white text-2xl font-bold">Método de pago</h2>
            </div>
            <button onClick={() => navigate('/')} className="glass-dark w-9 h-9 rounded-full flex items-center justify-center text-white">
              <X size={18} />
            </button>
          </div>

          {/* Event mini-resumen */}
          <div className="px-5 mb-5">
            <div className="glass rounded-2xl p-3 flex items-center gap-3">
              <img src="https://customer-assets.emergentagent.com/job_pixel-perfect-432/artifacts/wnp0mtdr_IMG_4964.jpeg" alt="" className="w-12 h-12 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-sm truncate">RailRave x Panamá</p>
                <div className="flex items-center gap-1.5 text-gray-400 text-xs">
                  <Calendar size={11} /> Lunes, 29 de Junio
                </div>
              </div>
              <span className="text-white font-bold">{total.toFixed(2)}€</span>
            </div>
          </div>

          {/* Express wallets */}
          <div className="px-5 mb-4 space-y-2">
            <button
              onClick={() => setMethod('apple')}
              className={`w-full py-3.5 rounded-2xl flex items-center justify-center gap-2 text-white font-semibold transition-all bg-black border ${method === 'apple' ? 'border-[#F82C47]' : 'border-white/15'}`}
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" /></svg>
              Apple Pay
            </button>
            <button
              onClick={() => setMethod('google')}
              className={`w-full py-3.5 rounded-2xl flex items-center justify-center gap-2 text-white font-semibold transition-all bg-black border ${method === 'google' ? 'border-[#F82C47]' : 'border-white/15'}`}
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.97 10.97 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.97 10.97 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
              Google Pay
            </button>
          </div>

          {/* Divider */}
          <div className="px-5 mb-4 flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-white/50 text-xs uppercase tracking-wider">o paga con tarjeta</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Card form (Payment Element look) */}
          <div className="px-5 mb-4 space-y-3">
            <div onClick={() => setMethod('card')} className={`glass rounded-2xl p-4 transition-all ${method === 'card' ? 'ring-2 ring-[#F82C47]' : ''}`}>
              <label className="text-white/60 text-xs font-medium uppercase tracking-wider block mb-1.5">Número de tarjeta</label>
              <div className="flex items-center gap-2">
                <CreditCard size={18} className="text-white/60" />
                <input
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e.target.value)}
                  className="bg-transparent border-none outline-none text-white text-base font-medium flex-1 caret-[#F82C47]"
                  style={{ fontFamily: 'ui-monospace, SF Mono, Monaco, monospace' }}
                />
                <div className="flex gap-1">
                  <div className="w-7 h-5 rounded bg-gradient-to-br from-[#1a1f71] to-[#0e57c2]" title="Visa" />
                  <div className="w-7 h-5 rounded bg-gradient-to-br from-[#eb001b] to-[#f79e1b]" title="Mastercard" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="glass rounded-2xl p-4">
                <label className="text-white/60 text-xs font-medium uppercase tracking-wider block mb-1.5">Caducidad</label>
                <input
                  value={exp}
                  onChange={(e) => setExp(e.target.value)}
                  placeholder="MM/YY"
                  className="bg-transparent border-none outline-none text-white text-base font-medium w-full caret-[#F82C47]"
                  style={{ fontFamily: 'ui-monospace, SF Mono, Monaco, monospace' }}
                />
              </div>
              <div className="glass rounded-2xl p-4">
                <label className="text-white/60 text-xs font-medium uppercase tracking-wider block mb-1.5">CVC</label>
                <div className="flex items-center gap-2">
                  <input
                    value={cvc}
                    onChange={(e) => setCvc(e.target.value)}
                    placeholder="123"
                    className="bg-transparent border-none outline-none text-white text-base font-medium flex-1 caret-[#F82C47]"
                    style={{ fontFamily: 'ui-monospace, SF Mono, Monaco, monospace' }}
                  />
                  <Lock size={14} className="text-white/40" />
                </div>
              </div>
            </div>

            <div className="glass rounded-2xl p-4">
              <label className="text-white/60 text-xs font-medium uppercase tracking-wider block mb-1.5">Nombre en la tarjeta</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-transparent border-none outline-none text-white text-base font-medium w-full caret-[#F82C47]"
              />
            </div>
          </div>

          {/* Summary */}
          <div className="px-5 mb-4">
            <div className="glass rounded-2xl p-4">
              <div className="flex justify-between items-center mb-1">
                <span className="text-white font-bold">Total</span>
                <span className="text-[#F82C47] font-bold text-xl">{total.toFixed(2)}€</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Puntos a ganar</span>
                <div className="glass-dark flex items-center gap-1 rounded-full px-2 py-0.5">
                  <Star size={12} className="text-white fill-white" />
                  <span className="text-white text-xs font-medium">+{points}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Pay button */}
          <div className="px-5 mb-3">
            <button className="w-full py-4 rounded-full overflow-hidden text-white font-bold text-lg" style={{ backgroundColor: '#F82C47' }}>
              <span className="flex items-center justify-center gap-2">
                <Lock size={18} /> Pagar {total.toFixed(2)}€ de forma segura
              </span>
            </button>
          </div>

          {/* === TRUST BADGE VARIANTS — pick one === */}
          <div className="px-5 pb-6">
            <p className="text-[#F82C47] text-[10px] uppercase tracking-widest font-bold mb-2 text-center">Variantes de trust badge</p>

            {/* Variante A — minimalista (actual) */}
            <div className="mb-4">
              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1.5 px-1">A · Minimalista (actual)</p>
              <div className="flex items-center justify-center gap-2 text-white/50 text-xs">
                <ShieldCheck size={12} /> Procesado por Stripe · Encriptado SSL
              </div>
            </div>

            {/* Variante B — pill destacada */}
            <div className="mb-4">
              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1.5 px-1">B · Pill destacada</p>
              <div className="flex items-center justify-center">
                <div className="glass-dark flex items-center gap-2 rounded-full px-4 py-2">
                  <ShieldCheck size={14} className="text-emerald-400" />
                  <span className="text-white text-xs font-semibold">Pago seguro</span>
                  <span className="text-white/40">·</span>
                  <span className="text-white/70 text-xs">Stripe SSL 256-bit</span>
                </div>
              </div>
            </div>

            {/* Variante C — fila con sellos visuales */}
            <div className="mb-4">
              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1.5 px-1">C · Con sellos / cards</p>
              <div className="glass rounded-2xl p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-1.5">
                    <ShieldCheck size={14} className="text-emerald-400" />
                    <span className="text-white text-xs font-semibold">Pago 100% seguro</span>
                  </div>
                  <span className="text-white/50 text-[10px]">SSL · 3D Secure</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <div className="px-2 py-1 rounded bg-white/10 text-white/80 text-[10px] font-bold tracking-wider">VISA</div>
                  <div className="px-2 py-1 rounded bg-white/10 text-white/80 text-[10px] font-bold tracking-wider">MASTERCARD</div>
                  <div className="px-2 py-1 rounded bg-white/10 text-white/80 text-[10px] font-bold tracking-wider"> Pay</div>
                  <div className="px-2 py-1 rounded bg-white/10 text-white/80 text-[10px] font-bold tracking-wider">G Pay</div>
                </div>
              </div>
            </div>

            {/* Variante D — bloque con copy + powered by */}
            <div className="mb-2">
              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1.5 px-1">D · Bloque con copy</p>
              <div className="glass-dark rounded-2xl p-3 flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
                  <ShieldCheck size={16} className="text-emerald-400" />
                </div>
                <div className="flex-1">
                  <p className="text-white text-xs font-bold">Tu pago está protegido</p>
                  <p className="text-white/60 text-[11px] leading-snug mt-0.5">Encriptación SSL 256-bit · 3D Secure · Procesado por Stripe</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMockupPreview;
